"use client";

import { useState, useCallback, useId, useRef } from "react";
import { useRouter } from "next/navigation";
import { SuggestDropdown } from "@/components/suggest-dropdown";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useSearchLogTimer } from "@/hooks/use-search-log-timer";

/**
 * プライマリカラーの相対輝度を計算し、ダークオーバーレイが必要かを判定する。
 * 相対輝度 0.4 以上の場合はダークオーバーレイを適用する。
 */
function needsDarkOverlay(hexColor: string): boolean {
  const hex = hexColor.replace("#", "");
  if (hex.length !== 6) return false;
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  // sRGB → linear
  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return luminance >= 0.4;
}

/**
 * HEX カラーを HSL に変換し、hue +20°・lightness -10% の終端色を返す。
 * グラデーション計算用。
 */
function computeGradientEndColor(hexColor: string): string {
  const hex = hexColor.replace("#", "");
  if (hex.length !== 6) return hexColor;
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    if (max === r) {
      h = ((g - b) / delta + 6) % 6;
    } else if (max === g) {
      h = (b - r) / delta + 2;
    } else {
      h = (r - g) / delta + 4;
    }
    h = h * 60;
  }

  const newH = (h + 20) % 360;
  const newL = Math.max(0, l * 100 - 10);
  const newS = s * 100;

  return `hsl(${newH.toFixed(0)}, ${newS.toFixed(1)}%, ${newL.toFixed(1)}%)`;
}

interface HeroSectionProps {
  primaryColor?: string;
  headerImageUrl?: string | null;
}

export function HeroSection({ primaryColor = "#00BCD4", headerImageUrl }: HeroSectionProps) {
  const [query, setQuery] = useState("");
  const [isSuggestOpen, setIsSuggestOpen] = useState(false);
  const router = useRouter();
  const heroListboxId = useId();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const formRef = useRef<HTMLFormElement>(null);
  const { logOnSubmit } = useSearchLogTimer({
    query: query.trim(),
    resultCount: null,
    source: "search",
  });

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const trimmed = query.trim();
      if (trimmed) {
        logOnSubmit();
        setIsSuggestOpen(false);
        router.push(`/search?q=${encodeURIComponent(trimmed)}`);
      }
    },
    [query, router, logOnSubmit],
  );

  const handleSuggestSelect = useCallback(
    (keyword: string) => {
      setQuery(keyword);
      setIsSuggestOpen(false);
      router.push(`/search?q=${encodeURIComponent(keyword)}`);
    },
    [router],
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsSuggestOpen(value.trim().length > 0);
  }, []);

  const handleSuggestClose = useCallback(() => {
    setIsSuggestOpen(false);
  }, []);

  const endColor = computeGradientEndColor(primaryColor);
  const showOverlay = needsDarkOverlay(primaryColor);

  const heroStyle: React.CSSProperties = headerImageUrl
    ? {
        backgroundImage: `url(${headerImageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
      }
    : {
        background: `linear-gradient(135deg, ${primaryColor} 0%, ${endColor} 100%)`,
        position: "relative",
      };

  return (
    <div style={heroStyle} className="hero-section w-full">
      {(showOverlay || !!headerImageUrl) && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            pointerEvents: "none",
          }}
        />
      )}
      <div
        className="relative mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center px-6 py-[80px] text-center tablet:py-[60px] mobile:py-[48px]"
        style={{ zIndex: 1 }}
      >
        <h1 className="mb-8 text-[32px] font-bold leading-[1.3] text-white tablet:text-[28px] mobile:text-[24px]">
          何かお困りですか？
        </h1>

        <form
          ref={formRef}
          role="search"
          action="/search"
          method="get"
          onSubmit={handleSubmit}
          className="relative w-full max-w-[640px] mobile:max-w-[calc(100%-48px)]"
        >
          <label htmlFor="hero-search" className="sr-only">
            キーワードで検索
          </label>
          <div
            className="hero-search-wrapper"
            style={{
              position: "relative",
              borderRadius: "12px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
              overflow: "hidden",
            }}
          >
            <svg
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-4 h-6 w-6 -translate-y-1/2"
              style={{ color: "#9CA3AF" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              id="hero-search"
              type="search"
              name="q"
              value={query}
              onChange={handleInputChange}
              onFocus={() => {
                if (query.trim().length > 0) setIsSuggestOpen(true);
              }}
              placeholder="キーワードで検索..."
              autoComplete="off"
              className="hero-search-input w-full bg-white pl-12 pr-4 text-[16px] text-gray-800 placeholder-[#9CA3AF] outline-none"
              style={{
                height: "56px",
                borderRadius: "12px",
                border: "none",
              }}
            />
          </div>
          <SuggestDropdown
            inputValue={query}
            onSelect={handleSuggestSelect}
            onClose={handleSuggestClose}
            isOpen={isSuggestOpen}
            listboxId={heroListboxId}
            isMobile={isMobile}
            anchorRef={formRef}
          />
        </form>
      </div>

      <style>{`
        @media (max-width: 767px) {
          .hero-section {
            min-height: 240px;
          }
          .hero-section .mx-auto {
            padding-top: 48px;
            padding-bottom: 48px;
          }
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .hero-section {
            min-height: 280px;
          }
        }
        @media (min-width: 1024px) {
          .hero-section {
            min-height: 320px;
          }
        }
        .hero-search-input:focus {
          outline: none;
          box-shadow: 0 4px 20px color-mix(in srgb, var(--theme-primary) 25%, transparent);
        }
        .hero-search-wrapper:focus-within {
          outline: 2px solid var(--theme-primary);
          outline-offset: 2px;
        }
      `}</style>
    </div>
  );
}
